
import { Ref, computed, createVNode, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { VxeButtonEvents, VxeTablePropTypes } from 'vxe-table';
import { OioButton, OioInput, OioMessage, OioModal, OioSelect, OioSpin } from '@kunlun/vue-ui-antd';
import { ExclamationCircleOutlined, TagFilled, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons-vue';
import XLSX from 'xlsx';
import { Modal } from 'ant-design-vue';
import { cloneDeep, debounce } from 'lodash-es';
import { http, getEasyImportTemplate } from '@kunlun/dependencies';
import { removeDecimalZero } from '../../../../utils/number';
import { vxeTableRemoveZeroFormatter } from '../../../../utils/fotmatter';
import { queryWorkbook } from '../../../../api';

export default defineComponent({
  name: 'AfterSalesExchangeGood',
  methods: { removeDecimalZero, vxeTableRemoveZeroFormatter },
  components: {
    OioButton,
    OioModal,
    OioInput,
    ExclamationCircleOutlined,
    TagFilled,
    FullscreenOutlined,
    FullscreenExitOutlined,
    OioSelect,
    OioSpin
  },
  props: ['fetchData', 'updateOne', 'exportFile', 'setFormData', '_formData', 'getGqlMutationName', 'mountedProcessFlag'],
  setup(props) {
    const sortOptions = ref([
      {
        value: 'order_desc',
        label: '按下单顺序排序'
      },
      {
        value: 'activity_group',
        label: '按活动分组排序'
      }
    ]);
    const priceTypeEnumList = ref<any>({
      EX_FACTORY_PRICE: '出厂价',
      PRICE: '一口价',
      REAL_PRICE: '实价',
      RETAIL_PRICE: '零售价'
    });
    const InventoryStatus = ref({
      ALL_SATISFIED: '全部满足',
      PARTIALLY_FULFIL: '部分满足',
      NOT_SATISFIED: '不满足'
    });

    const tableRef = ref<any>();
    const errorTableRef = ref<any>();
    const sortType = ref('order_desc');
    const errorData = ref<any[]>([]);
    const selectRow = ref<any>();
    const selectRecords = ref<any[]>([]);
    const isAllChecked = ref(false);
    const isIndeterminate = ref(false);
    const searchVal = ref('');
    const errorModal = ref<boolean>(false);
    const reasonOptions = ref<any[]>([]);
    const tips = ref('');
    const addLoading = ref(false);
    const tableLoading = ref(false);
    const importLoading = ref(false);
    const exportLoading = ref(false);
    const dataSource = ref<any[]>([]);
    const totalAmount = ref(0);

    const contentRef = ref();
    const isFullscreen = ref(false);
    const onFullscreenChange = () => {
      isFullscreen.value = Boolean(document.fullscreenElement);
    };
    onMounted(() => {
      document.addEventListener('fullscreenchange', onFullscreenChange);
    });
    onUnmounted(() => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    });
    const toggleFullscreen = () => {
      if (isFullscreen.value) {
        document.exitFullscreen();
      } else {
        contentRef.value?.requestFullscreen();
      }
    };
    const getPopupContainer = (triggerNode: HTMLElement) => {
      let curNode = triggerNode;
      while (curNode) {
        if (curNode.id === 'ASEG-content') {
          return curNode;
        }
        curNode = curNode.parentNode as HTMLElement;
      }
      return document.body;
    };

    const checkboxChangeEvent = () => {
      const $table = tableRef.value;
      if ($table) {
        isAllChecked.value = $table.isAllCheckboxChecked();
        isIndeterminate.value = $table.isAllCheckboxIndeterminate();
        selectRecords.value = $table.getCheckboxRecords();
      }
    };

    const beforeEditMethod = (scope) => {
      const isEmpty = !scope.row?.outCode;

      return ['申请售后数量', '包装规格'].includes(scope.column.title) && !isEmpty;
    };

    const exportDataEvent: VxeButtonEvents.Click = () => {
      try {
        exportLoading.value = true;
        props.exportFile(dataSource.value);
      } catch (error) {
        console.error(error);
      } finally {
        exportLoading.value = false;
      }
    };

    const importDataEvent = () => {
      // 弹出选择文件弹窗
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';

      // sheet_to_array的实现
      function parseExcelSheetToArrays(sheet) {
        const Range = {
          x1: sheet['!ref'].split(':')[0].match(/^[A-Z]+/)[0],
          y1: sheet['!ref'].split(':')[0].match(/\d+$/)[0],
          x2: sheet['!ref'].split(':')[1].match(/^[A-Z]+/)[0],
          y2: sheet['!ref'].split(':')[1].match(/\d+$/)[0]
        };

        const tableDataArr: any = [];
        for (let y = Range.y1; y <= Range.y2; y++) {
          // 行遍历
          const rowArr: any[] = [];
          // 商品名称列索引
          let nameIndex;
          for (let x = Range.x1.charCodeAt(); x <= Range.x2.charCodeAt(); x++) {
            // 列遍历 TD:暂时只支持到A~Z列，有需要的自己优化
            const position = String.fromCharCode(x) + y;
            // @ts-ignore
            rowArr.push(sheet?.[position]?.v ?? null);
            if (sheet?.[position]?.v?.includes?.('商品名称')) {
              nameIndex = x - Range.x1.charCodeAt();
            }
          }
          // 删除商品名称列
          if (nameIndex) {
            rowArr.splice(nameIndex, 1);
          }
          // 去除空行null
          if (rowArr.some((item) => item !== null)) {
            tableDataArr.push(rowArr);
          }
        }

        // 去除表头
        tableDataArr.shift();

        return tableDataArr;
      }

      const handleChangeFile = (e) => {
        const file = <File>e.target.files[0];
        const format = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
        const importFileFormat = ['xlsx', 'xls'];
        if (!importFileFormat.includes(format)) {
          OioMessage.error('仅支持上传excel文件');
        } else {
          const reader = new FileReader();
          reader.onload = async (e: any) => {
            try {
              const data = e.target.result;
              const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
              const sheetNames = workbook.SheetNames;
              const worksheet = workbook.Sheets[sheetNames[0]];
              // const sheetList = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false }) as any[];
              const arr = parseExcelSheetToArrays(worksheet);
              let str = '';
              arr.forEach((row, rowIndex) => {
                // if (rowIndex > 0) {
                row.forEach((col, colIndex) => {
                  if (colIndex !== row.length - 1) {
                    str += `${col}\t`;
                  } else {
                    str += `${col} `;
                  }
                });
                // }
              });
              importLoading.value = true;
              tableLoading.value = true;
              await queryData(str);
            } catch (error) {
              console.error(error);
            } finally {
              importLoading.value = false;
              tableLoading.value = false;
            }
          };
          reader.readAsBinaryString(file);
        }
        // 需要清除input的value，不然相同的文件无法上传多次
        if (input.value) {
          input.value = '';
        }
      };

      input.onchange = handleChangeFile;
      input.click();
    };

    const insertEvent = async (row: any) => {
      const $table = tableRef.value;
      if ($table) {
        const record = {};
        const { row: newRow } = await $table.insertAt(record, row);
        dataSource.value.push(newRow);
      }
    };

    const removeRow = ({ row, rowIndex }) => {
      Modal.confirm({
        title: '确认删除吗?',
        icon: createVNode(ExclamationCircleOutlined),
        async onOk() {
          tableLoading.value = true;
          try {
            dataSource.value.splice(rowIndex, 1);
            tableRef.value.reloadData(dataSource.value);
            if (dataSource.value.length === 0) {
              totalAmount.value = 0;
            }
            if (row.outCode) {
              const res = await props.updateOne(row);
              totalAmount.value = res.refundAmount ?? 0;
            }
          } finally {
            tableLoading.value = false;
          }
        }
      });
    };

    const queryData = async (str) => {
      searchVal.value = '';
      try {
        addLoading.value = true;
        tableLoading.value = true;

        const res = (await props.fetchData(str)) ?? {};
        dataSource.value = res.detailProxySaleSkus ?? [];
        tableRef.value.reloadData(dataSource.value);

        if (res.errMessages?.length) {
          tips.value = res.errMessages[0];
          const obj = {
            msg: res.errMessages[0]
          };
          errorData.value.unshift(...res.errMessages.map((msg) => ({ msg })));
          errorTableRef.value?.reloadData(errorData.value);
        }

        totalAmount.value = res.refundAmount;

        if (res?.isMerged) {
          OioMessage.info('添加时存在重复商品，商品数量已相加合并');
        }
      } finally {
        addLoading.value = false;
        tableLoading.value = false;
      }
    };

    const onChangeRow = async ({ row, rowIndex }) => {
      const copy = cloneDeep(dataSource.value);
      const sameRow = copy.find((r, i) => {
        return i !== rowIndex && r.outCode === row.outCode;
      });
      if (sameRow) {
        OioMessage.info('该商品已存在');
        throw new Error();
      }
      try {
        addLoading.value = true;
        tableLoading.value = true;
        dataSource.value.forEach((item, i) => {
          onSpecChange(i);
        });

        const res = await props.updateOne(row);
        if (res.errMessages?.length) {
          tips.value = res.errMessages[0];
          errorData.value.unshift(...res.errMessages.map((msg) => ({ msg })));
          errorTableRef.value?.reloadData(errorData.value);
          throw new Error(tips.value);
        } else {
          dataSource.value[rowIndex] = res.detailProxySaleSkus?.[res.detailProxySaleSkus?.length - 1] ?? {};
          tableRef.value.reloadData(dataSource.value);
        }
        totalAmount.value = res.refundAmount;
        tableRef.value?.clearEdit();
      } finally {
        addLoading.value = false;
        tableLoading.value = false;
      }
    };

    const onActivityChange = async (value, scope) => {
      scope.row.deliAriesActivities.forEach((item) => {
        item.isChecked = item.actName === value;
      });

      await onChangeRow(scope);
    };

    const onChangeReason = async (scope, val) => {
      scope.row.reason = props._formData.allReason?.find((item) => item.reason === val) ?? {};

      await onChangeRow(scope);
    };

    const showErrorModal = () => {
      errorModal.value = true;
    };

    const onSortChange = (value) => {
      // const copyData = cloneDeep(dataSource);
      // if (value === 'order_desc') {
      // } else {
      // }
      // props.reloadDataSource(copyData);
    };

    watch(
      dataSource,
      (value) => {
        props.setFormData(value);
      },
      { deep: true }
    );
    watch(
      () => props._formData.detailProxySaleSkus,
      (value) => {
        dataSource.value = value ?? [];
      },
      { deep: true, immediate: true }
    );
    watch(
      () => props._formData.refundAmount,
      (value) => {
        totalAmount.value = value ?? 0;
      },
      { immediate: true }
    );
    watch(
      () => props._formData.reverseType,
      () => {
        if (!props.mountedProcessFlag) {
          return;
        }
        dataSource.value = [];
        tableRef.value.reloadData(dataSource.value);
        totalAmount.value = 0;
      }
    );
    watch(
      () => props._formData.allReason,
      (value) => {
        reasonOptions.value = value?.map((item) => ({ label: item.reason, value: item.reason })) ?? [];
      },
      {
        immediate: true
      }
    );

    const allItem = ref<any[]>([]);
    const itemOptions = ref<any[]>([]);
    const query = ref('');
    const currentPage = ref(1);
    const onItemSearch = async (q: string, coverData = true) => {
      query.value = q;
      if (!props._formData.buyerId) {
        OioMessage.error('请将买卖家信息填写完整!');
        return;
      }
      if (!props._formData.reverseType) {
        OioMessage.error('缺少售后类型数据!');
        return;
      }
      if (!props._formData.reverseDisChannel) {
        OioMessage.error('请将分销渠道填写完整!');
        return;
      }
      if (!query.value) {
        clearItemOptions();
        return;
      }
      const reverseDisChannel = cloneDeep(props._formData.reverseDisChannel as any);
      reverseDisChannel && delete reverseDisChannel.__draftId;
      const gql = `
        mutation {
          ${props.getGqlMutationName()} {
            searchItemsByCondition(
              data: {
                buyerId: ${props._formData.buyerId}
                reverseType: ${props._formData.reverseType}
                reverseDisChannel: ${JSON.stringify(reverseDisChannel).replace(/"([a-zA-z_]+)":/g, (val, $1) => `${$1}:`)}
                detailProxySaleSkus:[{searchKey: "${query.value}"}]
              }
              pagination: {
                currentPage: ${currentPage.value},
                size: 20
              }
            ) {
              name
              outCode
            }
          }
        }
      `;
      const res = await http.query('deliAriesCoreSeller', gql);
      const _allItem = (res.data[props.getGqlMutationName()] as any).searchItemsByCondition ?? [];
      const _itemOptions =
        _allItem.map((item) => ({
          label: item.name,
          value: item.outCode
        })) ?? [];

      if (coverData) {
        allItem.value = _allItem;
        itemOptions.value = _itemOptions;
      } else {
        allItem.value.push(..._allItem);
        itemOptions.value.push(..._itemOptions);
      }
    };

    const onItemSearchDebounce = debounce(onItemSearch, 200);

    const clearItemOptions = () => {
      itemOptions.value = [];
      query.value = '';
      currentPage.value = 1;
    };

    const onSelectItemChange = async (outCode, scope) => {
      scope.row.outCode = outCode;
      try {
        await onChangeRow(scope);
      } catch (e) {
        scope.row.outCode = null;
        clearItemOptions();
        throw e;
      }
    };

    const loadMore = () => {
      currentPage.value += 1;
      onItemSearch(query.value, false);
    };

    const slipSelect = (event) => {
      const { scrollHeight, scrollTop, clientHeight } = event.target;
      // -1 兼容浏览器尺寸缩放
      if (scrollHeight - scrollTop - 1 <= clientHeight) {
        loadMore();
      }
    };

    const downloadTemplate = async () => {
      const paramsMap = {
        RETURN_MONEY_GOODS: {
          model: 'deli.aries.seller.DeliAriesSellerReverseDetailProxySaleSku',
          isExport: false,
          name: '申请退货商品导入(退货退款)'
        },
        RETURN_TRANSFER: {
          model: 'deli.aries.seller.DeliAriesSellerReverseDetailProxySaleSku',
          isExport: false,
          name: '申请退货商品导入(退换货)'
        }
      };
      const params = paramsMap[props._formData.reverseType];
      const res = (await queryWorkbook(params.model, params.isExport, params.name)).content;
      const urlObj: any = await getEasyImportTemplate(res[0].id);
      window.open(urlObj.redirectUri);
    };

    interface SpecificationData {
      count: any;
      conversionRules: string;
      id: string;
      pcsCount: number;
      unitCode: string;
      unitName: string;
    }

    function useSpecifications(_dataSource: Ref<any[]>) {
      const specificationDataListList = ref<SpecificationData[][]>([]);

      const updateDataListList = () => {
        specificationDataListList.value = _dataSource.value.map((item) => {
          return generateSpecListByRowData(item);
        });
      };

      const generateSpecListByRowData = (row) => {
        if (!row?.deliAriesItemSpecificationsProxyList) {
          return [];
        }
        const options = row.deliAriesItemSpecificationsProxyList;
        let { applyReverseNum: itemNum } = row;
        const copy = cloneDeep(options);
        // 顺序大到小，方便计算，后续通过reverse改为小到大，方便展示
        copy.sort((a, b) => b.pcsCount - a.pcsCount || (b.unitName > a.unitName ? 1 : -1));
        return (
          copy
            .filter((item) => item.isEnabled !== false)
            .map((spe) => {
              const count = Math.floor(itemNum / spe.pcsCount);
              itemNum -= count * spe.pcsCount;
              spe.count = count;
              return spe;
            })
            .reverse() ?? []
        );
      };

      watch(
        _dataSource,
        (val) => {
          updateDataListList();
        },
        { deep: true, immediate: true }
      );

      const onSpecChange = (rowIndex: number) => {
        const targetItemNum = specificationDataListList.value[rowIndex].reduce((acc, cur) => {
          return acc + cur.pcsCount * cur.count;
        }, 0);
        const row = _dataSource.value[rowIndex];
        if (!row?.deliAriesItemSpecificationsProxyList) {
          return;
        }
        // if (!row.outCode) {
        //   row.applyReverseNum = null;
        //   return;
        // }
        const targetSpec = generateSpecListByRowData({
          ...row,
          applyReverseNum: targetItemNum
        });
        row.deliAriesItemSpecificationsProxyList = targetSpec;
        row.applyReverseNum = targetItemNum;
      };

      return {
        specificationDataListList,
        onSpecChange
      };
    }

    const { specificationDataListList, onSpecChange } = useSpecifications(dataSource);

    const calcCellClassName = ({ row, rowIndex, $rowIndex, column, columnIndex, $columnIndex }) => {
      if (['包装规格'].includes(column.title)) {
        return 'toid-no-padding-cell';
      }
    };

    return {
      calcCellClassName,
      specificationDataListList,
      downloadTemplate,
      itemOptions,
      onItemSearch,
      clearItemOptions,
      onSelectItemChange,
      onItemSearchDebounce,
      slipSelect,
      beforeEditMethod,
      tableRef,
      errorData,
      errorTableRef,
      errorModal,
      selectRow,
      selectRecords,
      isAllChecked,
      isIndeterminate,
      searchVal,
      reasonOptions,
      onChangeReason,
      checkboxChangeEvent,
      exportDataEvent,
      importDataEvent,
      insertEvent,
      priceTypeEnumList,
      onChangeRow,
      queryData,
      showErrorModal,
      removeRow,
      sortOptions,
      tips,
      addLoading,
      tableLoading,
      importLoading,
      exportLoading,
      onSortChange,
      sortType,
      InventoryStatus,
      dataSource,
      totalAmount,
      isFullscreen,
      toggleFullscreen,
      contentRef,
      onActivityChange,
      getPopupContainer
    };
  }
});
